import { RG_TYPE, type Apartment } from "@project-rouge/rg-core";
import {
  getAboveLevelArea,
  getAboveLevelPerimeter,
  getAmenitiesArea,
  getAmenitiesPerimeter,
  getAmenitiesWallArea,
  getApartmentModuleCount,
  getArea,
  getBaseBuildDoubleDoorsCount,
  getBaseBuildMiscDoorsCount,
  getBaseBuildSingleDoorsCount,
  getCommercialArea,
  getCommercialPerimeter,
  getCommercialWallArea,
  getCountOfType,
  getDoubleDoorsCount,
  getFacadeExternalCornerLength,
  getFacadeInternalCornerLength,
  getFireCoreCount,
  getFootprint,
  getFoundationConcreteWeight,
  getFoundationSteelWeight,
  getGIA,
  getGlazedFacadeArea,
  getGrossFacadeArea,
  getLevelArea,
  getLevelCount,
  getLevelPerimeter,
  getLiftCoreCount,
  getLiftLevels,
  getNIA,
  getPerimeter,
  getPodiumGlazedFacadeArea,
  getPodiumGrossFacadeArea,
  getPodiumNetFacadeArea,
  getResidentialGlazedFacadeArea,
  getResidentialGrossFacadeArea,
  getResidentialNetFacadeArea,
  getRgObjectsAtLevel,
  getRgObjectsOfType,
  getRoofGrossFacadeArea,
  getRoofNetFacadeArea,
  getServicesArea,
  getServicesPerimeter,
  getServicesWallArea,
  getSingleDoorsCount,
  getSumExternalWallPerimeter,
  getVerticalWallArea,
} from "@project-rouge/rg-three";
import {
  type BuildingConfiguration,
  type ScenarioConfiguration,
  type UnitCategoryMix,
  type UnitCategoryWithQuantity,
} from "src/service/cost/data/building";
import type { RgBuilding } from "src/types/RgCorePackage";
import { type RgWorld } from "src/types/RgCorePackage";
import { MathSum } from "./MathSum";

/** @todo - this should be function imported from service-cost-client */
export function RgWorldToBeCostPayload(
  world: RgWorld,
  buildings: RgBuilding[]
): ScenarioConfiguration {
  const buildingsFootprintSum = MathSum(buildings.map((building) => getFootprint(building)));

  const scenarioSites = getRgObjectsOfType(world, RG_TYPE.Site);
  const scenarioArea = getArea(scenarioSites);
  return {
    scenario: {
      scenario_area: scenarioArea,
      scenario_area_net: scenarioArea - buildingsFootprintSum,
      scenario_perimeter: getPerimeter(scenarioSites),

      number_of_buildings: buildings.length,
    },

    buildings: buildings.map<BuildingConfiguration>((building) => ({
      units: getUnits(building),
      data: {
        nia: getNIA(building),
        gia: getGIA(building),

        footprint_area: getFootprint(building),

        core_and_corridor_area: getArea(building, [RG_TYPE.Core, RG_TYPE.Corridor]),
        core_and_corridor_area_ground_floor: getLevelArea(building, 0, [
          RG_TYPE.Core,
          RG_TYPE.Corridor,
        ]),
        core_and_corridor_area_ground_floor_above: getAboveLevelArea(building, 0, [
          RG_TYPE.Core,
          RG_TYPE.Corridor,
        ]),
        core_and_corridor_perimeter_ground_floor: getLevelPerimeter(building, 0, [
          RG_TYPE.Core,
          RG_TYPE.Corridor,
        ]),
        core_and_corridor_perimeter_ground_floor_above: getAboveLevelPerimeter(building, 0, [
          RG_TYPE.Core,
          RG_TYPE.Corridor,
        ]),

        core_area: getArea(building, [RG_TYPE.Core]),
        core_area_wall: getVerticalWallArea(building, [RG_TYPE.Core]),
        core_perimeter: getPerimeter(building, [RG_TYPE.Core]),

        corridor_area: getArea(building, [RG_TYPE.Corridor]),
        corridor_area_wall: getVerticalWallArea(building, [RG_TYPE.Corridor]),
        corridor_perimeter: getPerimeter(building, [RG_TYPE.Corridor]),

        amenity_locations_area: getAmenitiesArea(building),
        amenity_locations_area_wall: getAmenitiesWallArea(building),
        amenity_locations_perimeter: getAmenitiesPerimeter(building),

        commercial_locations_area: getCommercialArea(building),
        commercial_locations_area_wall: getCommercialWallArea(building),
        commercial_locations_perimeter: getCommercialPerimeter(building),

        service_locations_area: getServicesArea(building),
        service_locations_area_wall: getServicesWallArea(building),
        service_locations_perimeter: getServicesPerimeter(building),

        podium_facade_area: getPodiumGrossFacadeArea(building),
        podium_facade_area_net: getPodiumNetFacadeArea(building),
        podium_facade_glazed_area: getPodiumGlazedFacadeArea(building),

        facade_area: getGrossFacadeArea(building),
        facade_glazed_area: getGlazedFacadeArea(building),
        facade_external_corner_length: getFacadeExternalCornerLength(building),
        facade_internal_corner_length: getFacadeInternalCornerLength(building),
        substructure_concrete_weight: getFoundationConcreteWeight(building),
        substructure_steel_rebar_weight: getFoundationSteelWeight(building),

        external_wall_perimeter: getSumExternalWallPerimeter(building),

        roof_area: getArea(building, [RG_TYPE.Roof]),
        roof_perimeter: getPerimeter(building, [RG_TYPE.Roof]),
        roof_facade_area: getRoofGrossFacadeArea(building),
        roof_facade_area_net: getRoofNetFacadeArea(building),

        residential_facade_area: getResidentialGrossFacadeArea(building),
        residential_facade_area_net: getResidentialNetFacadeArea(building),
        residential_facade_glazed_area: getResidentialGlazedFacadeArea(building),

        number_of_entrances: getCountOfType(building, RG_TYPE.Entrance),
        number_of_doors_single: getBaseBuildSingleDoorsCount(building),
        number_of_doors_double: getBaseBuildDoubleDoorsCount(building),
        number_of_doors_misc: getBaseBuildMiscDoorsCount(building),
        number_of_module_doors_single: getSingleDoorsCount(building),
        number_of_module_doors_double: getDoubleDoorsCount(building),
        number_of_doors_entry_exit: getRgObjectsOfType(
          getRgObjectsOfType(building, [RG_TYPE.Entrance]),
          [RG_TYPE.Door]
        ).length,

        number_of_levels: getLevelCount(building, false),
        number_of_lift_levels: getLiftLevels(building),
        number_of_flights_of_stairs: getRgObjectsOfType(building, RG_TYPE.Core).length,

        number_of_units: getCountOfType(building, RG_TYPE.Apartment),
        number_of_modules: getApartmentModuleCount(building),

        number_of_cores: getRgObjectsAtLevel(building, [RG_TYPE.Core], 0).length,
        number_of_cores_lift: getLiftCoreCount(building, 0),
        number_of_cores_fire_fighting: getFireCoreCount(building, 0),
      },
    })),
  };
}

function getUnits(building: RgBuilding): UnitCategoryMix {
  type UnitName = string;
  type Quantity = number;

  const units = new Map<UnitName, Quantity>();

  for (const unit of getRgObjectsOfType<Apartment>(building, RG_TYPE.Apartment)) {
    units.set(unit.name, (units.get(unit.name) ?? 0) + 1);
  }

  return Array.from(units.entries()).map(([name, quantity]): UnitCategoryWithQuantity => {
    return {
      // This should be the unit name only, in the format of "scarlett 100" without versions.
      unit: name.split("_")[0],
      quantity,
    };
  });
}
